import React from 'react'
import { match } from 'ts-pattern'

import { ExitPromptType } from '@frame/ui-components/exit-prompt'

import { EventProperties, EventScope, TaskState, UnifiedSearchViewState } from '@frame/lib/api/events/types'
import { DirectTask, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { ItemKind } from '@frame/lib/hooks/useGetItems'

function convertToEmployerEventState(emp: DirectTask): TaskState {
  return {
    id: emp.id,
    employer_name: emp.employer_name,
    state: emp.state,
    strategy: emp.strategy,
  }
}

export function buildEmployerHistoryViewProps(
  canAddEmployer: boolean,
  bilEnabledStrategies: TrueworkJsStrategy[],
  employers: DirectTask[],
  index?: number
): EventProperties {
  return {
    internal_metadata: {
      views: {
        employer_view: {
          can_add_employer: canAddEmployer,
          tasks: employers.map(convertToEmployerEventState),
          bil_enabled_strategies: bilEnabledStrategies,
          index,
        },
      },
    },
  }
}

export function buildExitVerificationViewProps(
  scope: EventScope,
  pathname: string,
  type: ExitPromptType
): EventProperties {
  return {
    internal_metadata: {
      scope,
      views: {
        exit_verification_view: {
          current_pathname: pathname,
          exit_prompt_type: type,
        },
      },
    },
  }
}

export function buildSearchResultViewProps(search_view: UnifiedSearchViewState): EventProperties {
  const kind = search_view.item_kind
  const scope = match(kind)
    .with(ItemKind.EMPLOYER, () => {
      return EventScope.EMPLOYER
    })
    .with(ItemKind.GIG, () => {
      return EventScope.EMPLOYER
    })
    .with(ItemKind.PLATFORM, () => {
      return EventScope.PLATFORM
    })
    .with(ItemKind.BENEFITS, () => {
      return EventScope.PLATFORM
    })
    .with(ItemKind.INCOME, () => {
      return EventScope.SESSION
    }) // TODO AZALEA: not sure EventScope is correct here, need more context
    .exhaustive()

  return {
    internal_metadata: {
      scope,
      // Keeping backwards compatibility with the previous casing, but these are duplicative
      totalResults: search_view.total_results,
      clickedIndex: search_view.index,
      matchingScore: search_view.matching_score,
      views: {
        search_view,
      },
    },
  }
}

export function buildSearchViewProps(
  scope: EventScope,
  prefilled_employer?: DirectTask,
  prefilled_query?: string | null
): EventProperties {
  return {
    internal_metadata: {
      scope,
      views: {
        employer_search_view: {
          prefilled_employer: prefilled_employer ? convertToEmployerEventState(prefilled_employer) : undefined,
          prefilled_query,
        },
      },
    },
  }
}

function withScope(scope: EventScope, action: (scope: EventScope, e: React.MouseEvent) => void) {
  return (event: React.MouseEvent) => {
    action(scope, event)
  }
}

export function withEmployerScope(action: (scope: EventScope, e: React.MouseEvent) => void) {
  return withScope(EventScope.EMPLOYER, action)
}

export function withSessionScope(action: (scope: EventScope, e: React.MouseEvent) => void) {
  return withScope(EventScope.SESSION, action)
}
